import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Slider, useMediaQuery, useTheme } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { COLORS } from './constants';
import { ShimmerLine } from './LoadingAnimations';
import { RepairAsteroidsGame } from './RepairAsteroidsGame';
import { Tip } from './Tip';

import { Issue, IssueUrgency } from '@/models/Issue';
import { VALUE_FONT_SMALL } from '@/theme/consts';
import { getAverageIssueCost } from '@/utils/issues';

const REPAIR_RATIO_TIP =
  'Estimated repair cost as a percentage of list price. Industry benchmark: under 3% is move-in ready, 3–8% is typical, over 8% is high burden.';

type BucketId = 'now' | 'soon' | 'mid' | 'future';

interface BucketDef {
  id: BucketId;
  label: string;
  sub: string;
  days: number;
}

const BUCKETS: BucketDef[] = [
  { id: 'now', label: 'Now', sub: '< 30 days', days: 30 },
  { id: 'soon', label: 'Soon', sub: '1 – 3 mo', days: 60 },
  { id: 'mid', label: 'Mid', sub: '3 – 12 mo', days: 275 },
  { id: 'future', label: 'Later', sub: '1 yr+', days: 365 },
];

// Visual weight per bucket — sqrt(days) softens the linear day-span so the short
// near-term periods still get readable breathing room (linear pro-rata crushed
// "Now" to ~4% of canvas width).
const BUCKET_WEIGHT: Record<BucketId, number> = Object.fromEntries(
  BUCKETS.map((b) => [b.id, Math.sqrt(b.days)]),
) as Record<BucketId, number>;

const TOTAL_WEIGHT = BUCKETS.reduce((s, b) => s + BUCKET_WEIGHT[b.id], 0);

const BUCKET_LEFT_PCT: Record<BucketId, number> = (() => {
  const result = {} as Record<BucketId, number>;
  let cum = 0;
  BUCKETS.forEach((b) => {
    result[b.id] = cum / TOTAL_WEIGHT;
    cum += BUCKET_WEIGHT[b.id];
  });
  return result;
})();

const BUCKET_WIDTH_PCT: Record<BucketId, number> = Object.fromEntries(
  BUCKETS.map((b) => [b.id, BUCKET_WEIGHT[b.id] / TOTAL_WEIGHT]),
) as Record<BucketId, number>;

// Header-row-only column weights. "Now" carries the longest label relative to
// its pro-rata share ("Now / < 30 days"), which wraps to two lines at laptop
// card widths. Widen it slightly here so the header stays one line at every
// size; the bubble canvas below keeps using the unbiased BUCKET_WEIGHT so
// repair-timing positions stay accurate.
const HEADER_COLUMN_WEIGHT: Record<BucketId, number> = {
  ...BUCKET_WEIGHT,
  now: Math.min(BUCKET_WEIGHT.now + 1.5, BUCKET_WEIGHT.soon - 0.5),
};

const URGENCY_COLOR: Record<IssueUrgency, string> = {
  [IssueUrgency.Critical]: COLORS.critical,
  [IssueUrgency.Urgent]: COLORS.urgent,
  [IssueUrgency.Moderate]: COLORS.moderate,
  [IssueUrgency.Low]: COLORS.low,
  [IssueUrgency.Monitor]: COLORS.monitor,
};

interface PreparedIssue {
  id: string;
  title: string;
  category: string;
  urgency: IssueUrgency;
  cost: number;
  costMin?: number;
  costMax?: number;
  bucket: BucketId;
}

// Urgency → time-to-remediate, per URGENCY_TOOLTIPS in constants.ts:
//   Critical = address before moving in       → Now    (< 30 days)
//   Urgent   = within the first 30 days       → Soon   (1 – 3 mo)
//   Moderate = within the first year          → Mid    (3 – 12 mo)
//   Low      = at your convenience            → Later  (1 yr+)
//   Monitor  = may need attention in 2–5 yrs  → Later  (1 yr+)
const URGENCY_BUCKET: Record<IssueUrgency, BucketId> = {
  [IssueUrgency.Critical]: 'now',
  [IssueUrgency.Urgent]: 'soon',
  [IssueUrgency.Moderate]: 'mid',
  [IssueUrgency.Low]: 'future',
  [IssueUrgency.Monitor]: 'future',
};

const bucketForIssue = (issue: Issue): BucketId => URGENCY_BUCKET[issue.urgency] ?? 'future';

const formatK = (value: number): string => {
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
};

interface TooltipState {
  issue: PreparedIssue | null;
  x: number;
  y: number;
}

const FloatingTooltip = ({ state }: { state: TooltipState }) => {
  if (!state.issue) return null;
  const it = state.issue;
  const PAD = 12;
  const TIP_WIDTH = Math.min(320, window.innerWidth - PAD * 2);
  // Clamp to viewport
  const left = Math.max(PAD, Math.min(state.x + 14, window.innerWidth - TIP_WIDTH - PAD));
  const top = Math.max(PAD, state.y - 10);
  const isBigTicket = it.cost >= 5000;
  return createPortal(
    <div
      style={{
        position: 'fixed',
        left,
        top,
        zIndex: 9999,
        background: 'var(--tx-glass-panel)',
        backgroundImage: 'var(--tx-surface-light)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: 'var(--tx-shadow)',
        border: 'none',
        borderRadius: '12px',
        padding: '14px 16px',
        width: TIP_WIDTH,
        pointerEvents: 'none',
      }}>
      {/* Title + urgency chip */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
          marginBottom: 6,
        }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: COLORS.text,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}>
          {it.title}
        </span>
        <span
          style={{
            padding: '3px 10px',
            borderRadius: 6,
            background: `${URGENCY_COLOR[it.urgency]}1a`,
            color: URGENCY_COLOR[it.urgency],
            fontSize: 10,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
          {it.urgency}
        </span>
      </div>
      {/* Category + Big Ticket */}
      <div style={{ fontSize: 12, color: COLORS.textDim, marginBottom: 10 }}>
        {it.category}
        {isBigTicket && <span style={{ color: COLORS.nhdAccent }}> · Big Ticket</span>}
      </div>
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginBottom: 8 }} />
      {/* Est. Impact + Cost range */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: COLORS.textDim }}>
          Est. Impact: ${it.cost.toLocaleString()}
        </span>
        {(it.costMin != null || it.costMax != null) && (
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>
            ${(it.costMin ?? 0).toLocaleString()} – ${(it.costMax ?? 0).toLocaleString()}
          </span>
        )}
      </div>
    </div>,
    document.body,
  );
};

const CANVAS_PAD_X = 20;
const CANVAS_HEIGHT = 120;
const MIN_RADIUS = 4;
const MAX_RADIUS = 32;
// Lower exponent compresses, higher expands. 0.7 keeps tiny items visible while
// letting expensive items dominate.
const COST_SCALE_EXPONENT = 0.7;
const CIRCLE_OPACITY = 0.55;
// Tight containment: bubbles drift at most this far from the centerline. Big bubbles
// stay near the middle; only small ones reach the outer edge of the band.
const VERTICAL_AMPLITUDE = 28;
// Visual nudge — pushes the bubble cluster down inside the canvas without changing
// canvas height or surrounding elements.
const BUBBLE_Y_OFFSET = 25;

// Per-issue horizontal jitter (as fraction of an "average slot width"). Adds
// organic wiggle without breaking the band order.
const X_JITTER_FRACTION = 0.35;

interface PlacedIssue extends PreparedIssue {
  x: number; // pixel center x
  y: number; // pixel center y
  radius: number;
}

const stableHash = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return h;
};

// Cost → radius via configurable power scale. Higher exponent = more contrast
// between expensive and cheap items.
const costToRadius = (cost: number, minCost: number, maxCost: number): number => {
  if (maxCost <= minCost) return (MIN_RADIUS + MAX_RADIUS) / 2;
  const norm = Math.max(0, cost - minCost) / (maxCost - minCost);
  const t = Math.pow(norm, COST_SCALE_EXPONENT);
  return MIN_RADIUS + t * (MAX_RADIUS - MIN_RADIUS);
};

interface BandExtent {
  id: BucketId;
  leftPct: number; // 0..1 of canvas width
  rightPct: number;
  centerPct: number;
}

// Bubble-field placement: each circle positioned by urgency-band center + cost
// rank within band. Vertical position is jittered around the centerline using a
// stable per-issue hash. Circles can overlap; partial opacity blends them into a
// continuous color field.
const packIssues = (
  issues: PreparedIssue[],
  innerWidth: number,
): { placed: PlacedIssue[]; canvasHeight: number; bandExtents: BandExtent[] } => {
  if (issues.length === 0 || innerWidth <= 0) {
    return { placed: [], canvasHeight: CANVAS_HEIGHT, bandExtents: [] };
  }

  const costs = issues.map((i) => i.cost);
  const minCost = Math.min(...costs);
  const maxCost = Math.max(...costs);
  const centerY = CANVAS_HEIGHT / 2;

  // Within each bucket, sort by cost desc so the most expensive item anchors the
  // bucket's left edge. Then each bubble gets a slot proportional to the bucket's
  // pro-rata column width (pixel range = bucketLeftPct..bucketRightPct of canvas).
  const placed: PlacedIssue[] = [];

  BUCKETS.forEach((bucket) => {
    const bucketItems = issues
      .filter((i) => i.bucket === bucket.id)
      .sort((a, b) => b.cost - a.cost);
    if (bucketItems.length === 0) return;

    const leftPx = BUCKET_LEFT_PCT[bucket.id] * innerWidth;
    const widthPx = BUCKET_WIDTH_PCT[bucket.id] * innerWidth;
    const slotWidth = widthPx / bucketItems.length;

    bucketItems.forEach((issue, idx) => {
      const radius = costToRadius(issue.cost, minCost, maxCost);
      const h = stableHash(issue.id);

      const jx = ((h % 1000) / 1000) * 2 - 1; // -1..+1
      const baseX = leftPx + (idx + 0.5) * slotWidth;
      const jitterX = jx * slotWidth * X_JITTER_FRACTION;
      const x = Math.max(radius, Math.min(innerWidth - radius, baseX + jitterX));

      const jy = (((h >> 8) % 1000) / 1000) * 2 - 1;
      const sizeBias = 1 - (radius - MIN_RADIUS) / (MAX_RADIUS - MIN_RADIUS);
      const ampFactor = 0.2 + 0.8 * sizeBias;
      const y = centerY + BUBBLE_Y_OFFSET + jy * VERTICAL_AMPLITUDE * ampFactor;

      placed.push({ ...issue, x, y, radius });
    });
  });

  // Band extents = the pro-rata column ranges (independent of issue counts)
  const bandExtents: BandExtent[] = BUCKETS.map((b) => ({
    id: b.id,
    leftPct: BUCKET_LEFT_PCT[b.id],
    rightPct: BUCKET_LEFT_PCT[b.id] + BUCKET_WIDTH_PCT[b.id],
    centerPct: BUCKET_LEFT_PCT[b.id] + BUCKET_WIDTH_PCT[b.id] / 2,
  }));

  // Render largest first so smaller circles paint on top
  placed.sort((a, b) => b.radius - a.radius);

  return { placed, canvasHeight: CANVAS_HEIGHT, bandExtents };
};

const FlowCanvas = ({
  prepared,
  bucketed,
  grandTotal,
  setTooltip,
  isMobile,
}: {
  prepared: PreparedIssue[];
  bucketed: Record<BucketId, PreparedIssue[]>;
  grandTotal: number;
  setTooltip: (state: TooltipState) => void;
  isMobile: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(620);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const { placed, canvasHeight } = useMemo(() => packIssues(prepared, width), [prepared, width]);

  return (
    <div>
      {/* 4-period header grid — column widths pro-rata to each period's day-span */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: BUCKETS.map((b) => `${HEADER_COLUMN_WEIGHT[b.id]}fr`).join(' '),
          width: '100%',
        }}>
        {BUCKETS.map((b, i) => {
          const total = bucketed[b.id].reduce((s, item) => s + item.cost, 0);
          const pct = grandTotal > 0 ? Math.round((total / grandTotal) * 100) : 0;
          const isFirst = i === 0;
          const isLast = i === BUCKETS.length - 1;
          const innerPad = isMobile ? 6 : 20;
          const padLeft = isFirst ? 20 : innerPad;
          const padRight = isLast ? 20 : innerPad;
          const padTop = isMobile ? 10 : 12;
          return (
            <div
              key={b.id}
              style={{
                paddingTop: padTop,
                paddingRight: padRight,
                paddingBottom: 0,
                paddingLeft: padLeft,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}>
              {i < BUCKETS.length - 1 && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: isMobile ? 16 : 18,
                    bottom: 0,
                    width: '0.5px',
                    background: 'var(--tx-fill)',
                  }}
                />
              )}
              <div style={{ whiteSpace: 'nowrap' }}>
                <span
                  style={{
                    fontSize: isMobile ? 11 : 12,
                    fontWeight: 600,
                    color: 'var(--tx-text)',
                  }}>
                  {b.label}
                </span>
                {!isMobile && (
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)' }}> {b.sub}</span>
                )}
              </div>
              <div
                style={{
                  fontFamily: VALUE_FONT_SMALL,
                  // the window total as a light figure (the dashboards' 200-weight, tight-tracked numerals)
                  fontSize: isMobile ? 20 : 28,
                  fontWeight: 200,
                  letterSpacing: '-0.05em',
                  lineHeight: 1.1,
                  color: COLORS.text,
                  marginTop: isMobile ? 4 : 6,
                }}>
                {formatK(total)}
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'var(--tx-muted)',
                  marginTop: isMobile ? 3 : 4,
                  whiteSpace: 'nowrap',
                }}>
                {bucketed[b.id].length}
                {isMobile ? '' : ' item'}
                {!isMobile && (bucketed[b.id].length !== 1 ? 's' : '')} · {pct}%
              </div>
            </div>
          );
        })}
      </div>

      {/* Continuous bubble canvas + zoom slider */}
      <div
        style={{
          padding: `0 ${CANVAS_PAD_X}px`,
          marginTop: isMobile ? 20 : 24,
          display: 'flex',
          alignItems: 'stretch',
          gap: 12,
        }}>
        <div
          ref={ref}
          style={{
            position: 'relative',
            flex: 1,
            height: canvasHeight,
          }}>
          {placed.map((p) => {
            const effectiveZoom = isMobile ? 1 : zoom;
            const r = p.radius * effectiveZoom;
            const isBigTicket = p.cost >= 5000;
            // Vary both duration AND delay per bubble so big-ticket bubbles don't
            // breathe in lockstep — duration 1.8s..3.4s, delay 0..2.6s.
            const h = Math.abs(stableHash(p.id));
            const pulseDuration = isBigTicket ? 1.0 + (h % 1000) / 1000 : 0;
            const pulseDelay = isBigTicket ? ((h >> 8) % 2600) / 1000 : 0;
            return (
              <div
                key={p.id}
                className={isBigTicket ? 'rh-pulse' : undefined}
                onMouseEnter={(e) => setTooltip({ issue: p, x: e.clientX, y: e.clientY })}
                onMouseMove={(e) => setTooltip({ issue: p, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setTooltip({ issue: null, x: 0, y: 0 })}
                style={{
                  position: 'absolute',
                  left: p.x - r,
                  top: p.y - r,
                  width: r * 2,
                  height: r * 2,
                  borderRadius: '50%',
                  background: URGENCY_COLOR[p.urgency],
                  opacity:
                    p.urgency === IssueUrgency.Monitor ? CIRCLE_OPACITY * 0.6 : CIRCLE_OPACITY,
                  mixBlendMode: 'screen',
                  cursor: 'pointer',
                  transition:
                    'transform 0.12s, opacity 0.12s, width 0.15s, height 0.15s, left 0.15s, top 0.15s',
                  ...(isBigTicket
                    ? {
                        animation: `rhBubblePulse ${pulseDuration}s ${pulseDelay}s infinite ease-in-out`,
                      }
                    : null),
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.animationPlayState = 'paused';
                  e.currentTarget.style.transform = 'scale(1.18)';
                  e.currentTarget.style.opacity = '0.95';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.animationPlayState = 'running';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.opacity = String(
                    p.urgency === IssueUrgency.Monitor ? CIRCLE_OPACITY * 0.6 : CIRCLE_OPACITY,
                  );
                }}
              />
            );
          })}
        </div>
        {!isMobile && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              width: 28,
            }}>
            <Slider
              orientation="vertical"
              value={zoom}
              min={0.4}
              max={2.5}
              step={0.05}
              onChange={(_, v) => setZoom(typeof v === 'number' ? v : v[0])}
              aria-label="Zoom bubbles"
              sx={{
                height: canvasHeight - 16,
                color: COLORS.accent2,
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                  backgroundColor: COLORS.text,
                  '&:hover, &.Mui-focusVisible, &.Mui-active': {
                    boxShadow: `0 0 0 6px ${COLORS.accent2}33`,
                  },
                },
                '& .MuiSlider-rail': {
                  opacity: 0.25,
                },
                '& .MuiSlider-track': {
                  opacity: 0.6,
                  border: 'none',
                },
              }}
            />
            <div style={{ fontSize: 10, color: COLORS.accent2, opacity: 0.6 }}>
              {zoom.toFixed(1)}×
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const hexToRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const blendBucketColor = (issues: PreparedIssue[]): string => {
  const total = issues.reduce((s, i) => s + i.cost, 0);
  if (!total) return COLORS.monitor;
  let r = 0;
  let g = 0;
  let b = 0;
  issues.forEach((it) => {
    const w = it.cost / total;
    const [ir, ig, ib] = hexToRgb(URGENCY_COLOR[it.urgency]);
    r += ir * w;
    g += ig * w;
    b += ib * w;
  });
  return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
};

const SpendWave = ({
  bucketed,
  playKey,
  height = 56,
  noAnimate = false,
}: {
  bucketed: Record<BucketId, PreparedIssue[]>;
  /** Increment to retrigger the rug-pull + bounce animation (e.g. card hover). */
  playKey: number;
  /** SVG height in viewBox units. Default 56. */
  height?: number;
  /** Skip the rise animation — render fully settled. */
  noAnimate?: boolean;
}) => {
  const W = 800;
  const H = height;
  const baseline = H;
  const maxAmp = Math.max(0, H - 8);

  // Peak positions = pro-rata bucket centers (matches the headers + bubble layout)
  const cx = BUCKETS.map((b) => (BUCKET_LEFT_PCT[b.id] + BUCKET_WIDTH_PCT[b.id] / 2) * W);

  const bucketCosts = BUCKETS.map((b) => bucketed[b.id].reduce((s, i) => s + i.cost, 0));
  const maxCost = Math.max(...bucketCosts, 1);
  const amps = bucketCosts.map((c) => (c / maxCost) * maxAmp);
  const finalPeaks = cx.map((x, i) => ({ x, y: baseline - amps[i] }));

  // Ship-in-a-bottle slosh: a traveling disturbance bounces left→right→left along
  // the wave with amplified per-peak amplitude that decays out, settling on the
  // final data shape. ~2.2s.
  const ANIM_MS = 900;
  const [phase, setPhase] = useState(1);
  useEffect(() => {
    if (noAnimate) {
      setPhase(1);
      return;
    }
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setPhase(1);
      return;
    }
    setPhase(0);
    let raf = 0;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ANIM_MS);
      setPhase(t);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playKey, noAnimate]);

  // Single rise from flat baseline → final shape with smooth ease-out. No
  // bouncing or repeated rollovers.
  const easeOut = (x: number) => 1 - Math.pow(1 - x, 3);
  const rise = easeOut(phase);

  // Cosine interpolation through the 4 anchor peaks → continuous static curve
  const controlPts = [{ x: 0, y: baseline }, ...finalPeaks, { x: W, y: baseline }];
  const splineY = (x: number): number => {
    for (let i = 0; i < controlPts.length - 1; i++) {
      const a = controlPts[i];
      const b = controlPts[i + 1];
      if (x >= a.x && x <= b.x) {
        const t = (x - a.x) / (b.x - a.x);
        const ts = (1 - Math.cos(t * Math.PI)) / 2; // smooth-step
        return a.y + (b.y - a.y) * ts;
      }
    }
    return controlPts[controlPts.length - 1].y;
  };

  // Sample densely so the rise reads as one continuous smooth curve
  const N_SAMPLES = 80;
  const samples: { x: number; y: number }[] = [];
  for (let i = 0; i <= N_SAMPLES; i++) {
    const x = (i / N_SAMPLES) * W;
    const finalY = splineY(x);
    const y = baseline + (finalY - baseline) * rise;
    samples.push({ x, y });
  }

  const d = `M ${samples[0].x.toFixed(2)} ${samples[0].y.toFixed(2)} ${samples
    .slice(1)
    .map((s) => `L ${s.x.toFixed(2)} ${s.y.toFixed(2)}`)
    .join(' ')}`;

  // Gradient stops at pro-rata bucket centers so the color flow tracks the bubbles
  const blended = BUCKETS.map((b) => blendBucketColor(bucketed[b.id]));
  const centerOffsetPct = (idx: number) =>
    `${((BUCKET_LEFT_PCT[BUCKETS[idx].id] + BUCKET_WIDTH_PCT[BUCKETS[idx].id] / 2) * 100).toFixed(
      2,
    )}%`;
  const stops = [
    { offset: '0%', color: blended[0] },
    { offset: centerOffsetPct(0), color: blended[0] },
    { offset: centerOffsetPct(1), color: blended[1] },
    { offset: centerOffsetPct(2), color: blended[2] },
    { offset: centerOffsetPct(3), color: blended[3] },
    { offset: '100%', color: blended[3] },
  ];

  return (
    <svg
      width="100%"
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}>
      <defs>
        <linearGradient id="repair-horizon-stroke" x1="0%" y1="0%" x2="100%" y2="0%">
          {stops.map((s, i) => (
            <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity="0.75" />
          ))}
        </linearGradient>
        <linearGradient id="repair-horizon-fill" x1="0%" y1="0%" x2="100%" y2="0%">
          {stops.map((s, i) => (
            <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity="0.1" />
          ))}
        </linearGradient>
      </defs>
      <path d={`${d} L${W} ${baseline} L0 ${baseline} Z`} fill="url(#repair-horizon-fill)" />
      <path
        d={d}
        fill="none"
        stroke="url(#repair-horizon-stroke)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="0"
        y1={baseline}
        x2={W}
        y2={baseline}
        stroke="rgba(187,183,175,0.1)"
        strokeWidth="0.5"
      />
    </svg>
  );
};

export interface RepairHorizonProps {
  issues?: Issue[];
  isLoading?: boolean;
  /** Repair-cost-to-list-price ratio as a percentage (e.g. 1.4 = 1.4%). Hidden if undefined. */
  repairPriceRatio?: number | null;
  /** When true, renders a chevron in the heading and folds the body. */
  collapsible?: boolean;
  /** localStorage key used to persist open/closed state across sessions. */
  storageKey?: string;
  /** Default open state on first visit (no localStorage entry yet). Defaults to false. */
  defaultOpen?: boolean;
  /** Property's current EQ Home Score (0–100). Forwarded to the in-card
   *  RepairAsteroidsGame so the round starts from the real score. */
  conditionScore?: number | null;
}

export const RepairHorizon = ({
  issues,
  isLoading = false,
  repairPriceRatio,
  collapsible = false,
  storageKey,
  defaultOpen = false,
  conditionScore,
}: RepairHorizonProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (!collapsible) return true;
    if (!storageKey) return defaultOpen;
    try {
      const stored = localStorage.getItem(storageKey);
      return stored == null ? defaultOpen : stored === '1';
    } catch {
      return defaultOpen;
    }
  });
  useEffect(() => {
    if (!collapsible || !storageKey) return;
    try {
      localStorage.setItem(storageKey, isOpen ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsible, storageKey, isOpen]);
  const [tooltip, setTooltip] = useState<TooltipState>({ issue: null, x: 0, y: 0 });
  const [gameOpen, setGameOpen] = useState(false);
  // Bumped each time the card is hovered → re-keys the wave path so its
  // stroke-dashoffset draw animation replays.
  const [wavePlayKey, setWavePlayKey] = useState(0);

  const prepared = useMemo<PreparedIssue[]>(
    () =>
      (issues ?? [])
        .filter((i) => !i.resolved)
        .map((i) => ({
          id: i.id,
          title: i.title,
          category: i.trade ?? i.category ?? 'Other',
          urgency: i.urgency,
          cost: getAverageIssueCost(i),
          costMin: i.costEstimateMin,
          costMax: i.costEstimateMax,
          bucket: bucketForIssue(i),
        }))
        .filter((i) => i.cost > 0),
    [issues],
  );

  const bucketed = useMemo<Record<BucketId, PreparedIssue[]>>(() => {
    const map: Record<BucketId, PreparedIssue[]> = {
      now: [],
      soon: [],
      mid: [],
      future: [],
    };
    prepared.forEach((i) => map[i.bucket].push(i));
    return map;
  }, [prepared]);

  const grandTotal = prepared.reduce((s, i) => s + i.cost, 0);
  const nearTermTotal = bucketed.now.concat(bucketed.soon).reduce((s, i) => s + i.cost, 0);

  if (isLoading) {
    return (
      <div
        style={{
          background: COLORS.card,
          borderRadius: 25,
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          overflow: 'hidden',
          padding: '20px 0',
        }}>
        <div style={{ padding: '0 20px 14px' }}>
          <div style={{ fontSize: 17, fontWeight: 300, color: COLORS.text, marginBottom: 6 }}>
            Repair horizon
          </div>
          <ShimmerLine width={260} height={12} />
        </div>
        <div style={{ padding: '0 20px' }}>
          <ShimmerLine width="100%" height={140} borderRadius={12} />
        </div>
      </div>
    );
  }

  const hasIssues = prepared.length > 0;

  const isCollapsed = collapsible && !isOpen;
  return (
    <div
      onMouseEnter={() => setWavePlayKey((k) => k + 1)}
      style={{
        padding: '20px 0',
        background: COLORS.card,
        borderRadius: 25,
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: isCollapsed ? undefined : '100%',
      }}>
      <style>{`
        @keyframes rhBubblePulse {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.1); filter: brightness(1.4); }
        }
        @media (prefers-reduced-motion: reduce) {
          .rh-pulse { animation: none !important; }
        }
      `}</style>
      {/* Top */}
      <div
        onClick={collapsible ? () => setIsOpen((o) => !o) : undefined}
        style={{
          display: 'flex',
          // Collapsed: row so chevron pins top-right. Expanded: original
          // responsive layout (column on mobile so title/sub stack above
          // the triptych and bubbles below; row on desktop).
          flexDirection: isCollapsed ? 'row' : isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: isCollapsed ? 0 : 16,
          padding: '0 20px',
          gap: isMobile ? 12 : 16,
          flexWrap: 'wrap',
          cursor: collapsible ? 'pointer' : 'default',
          position: 'relative',
        }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 400,
              letterSpacing: '-0.015em',
              color: COLORS.text,
              fontFamily: "'Manrope',sans-serif",
              lineHeight: 1.2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}>
            Repair horizon
            {hasIssues && (
              <Tip text="Play Game" placement="top" style={{ borderBottom: 'none', lineHeight: 0 }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGameOpen(true);
                  }}
                  aria-label="Play Game"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 4,
                    margin: 0,
                    lineHeight: 0,
                    cursor: 'pointer',
                    color: 'var(--tx-lime-ink)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <line x1="6" y1="11" x2="10" y2="11" />
                    <line x1="8" y1="9" x2="8" y2="13" />
                    <line x1="15" y1="12" x2="15.01" y2="12" />
                    <line x1="18" y1="10" x2="18.01" y2="10" />
                    <path
                      d={
                        'M17.32 5H6.68a4 4 0 0 0-3.978 3.59' +
                        'c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16' +
                        'a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414' +
                        'A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18' +
                        'c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258' +
                        'A4 4 0 0 0 17.32 5z'
                      }
                    />
                  </svg>
                </button>
              </Tip>
            )}
          </div>
          {collapsible && !isOpen && (
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: 'var(--tx-muted)',
                fontFamily: "'Manrope',sans-serif",
                lineHeight: 1.2,
                marginTop: 4,
              }}>
              Home health and repair planning across a timeline.
            </div>
          )}
          {(!collapsible || isOpen) && (
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: 'var(--tx-muted)',
                lineHeight: 1.5,
                marginTop: 6,
                maxWidth: 480,
              }}>
              Each bubble is a repair item. Size shows estimated cost, color shows urgency, and
              position along the timeline shows when to address it.
            </div>
          )}
        </div>
        {(!collapsible || isOpen) && (
          <div
            style={{
              display: 'flex',
              // the three stats read as separate figures: 40px apart on a computer
              gap: isMobile ? 16 : 40,
              alignSelf: isMobile ? 'stretch' : 'auto',
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
              flexWrap: 'wrap',
              marginTop: isMobile ? 10 : 0,
              // Reserve room for the absolute-positioned chevron at top-right.
              paddingRight: collapsible ? 28 : 0,
            }}>
            <div style={{ textAlign: 'left' }}>
              <div
                style={{
                  fontFamily: VALUE_FONT_SMALL,
                  fontSize: 22,
                  fontWeight: 200,
                  letterSpacing: '-0.05em',
                  lineHeight: 1.1,
                  color: COLORS.text,
                }}>
                ${grandTotal.toLocaleString()}
              </div>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 4 }}>Total est. cost</div>
            </div>
            {repairPriceRatio != null && (
              <div style={{ textAlign: 'left' }}>
                <div
                  style={{
                    fontFamily: VALUE_FONT_SMALL,
                    fontSize: 22,
                    fontWeight: 200,
                    letterSpacing: '-0.05em',
                    lineHeight: 1.1,
                    color: COLORS.text,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}>
                  {repairPriceRatio.toFixed(1)}%
                  <Tip text={REPAIR_RATIO_TIP} style={{ borderBottom: 'none', lineHeight: 0 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: COLORS.accent2, opacity: 0.7 }} />
                  </Tip>
                </div>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 4 }}>Repair / price</div>
              </div>
            )}
            <div style={{ textAlign: 'left' }}>
              <div
                style={{
                  fontFamily: VALUE_FONT_SMALL,
                  fontSize: 22,
                  fontWeight: 200,
                  letterSpacing: '-0.05em',
                  lineHeight: 1.1,
                  color: COLORS.urgent,
                }}>
                ${nearTermTotal.toLocaleString()}
              </div>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 4 }}>Due in 90 days</div>
            </div>
          </div>
        )}
        {collapsible && (
          <span
            style={{
              color: COLORS.accent2,
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              transform: isOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform .2s',
              flexShrink: 0,
              position: 'absolute',
              top: 0,
              right: 20,
            }}>
            <ChevronDown size={18} />
          </span>
        )}
      </div>

      {/* Collapsed: full-width spend wave on its own line, flush with the
          card's 20px bottom padding edge. */}
      {collapsible && !isOpen && hasIssues && (
        <div style={{ padding: '0 20px', marginTop: 12 }}>
          <SpendWave bucketed={bucketed} playKey={0} height={32} noAnimate />
        </div>
      )}

      {(!collapsible || isOpen) &&
        (!hasIssues ? (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              fontSize: 12,
              color: COLORS.textDim,
            }}>
            No repair items yet.
          </div>
        ) : (
          <>
            <FlowCanvas
              prepared={prepared}
              bucketed={bucketed}
              grandTotal={grandTotal}
              setTooltip={setTooltip}
              isMobile={isMobile}
            />

            <div
              style={{
                marginTop: 'auto',
                paddingTop: isMobile ? 16 : 24,
                paddingLeft: 20,
                paddingRight: 20,
              }}>
              <SpendWave bucketed={bucketed} playKey={wavePlayKey} />
            </div>
          </>
        ))}

      <FloatingTooltip state={tooltip} />
      <RepairAsteroidsGame
        open={gameOpen}
        onClose={() => setGameOpen(false)}
        issues={issues}
        startScore={conditionScore}
      />
    </div>
  );
};

export default RepairHorizon;
